import React from 'react';
import PropTypes from 'prop-types';
import { QueryClientProvider, QueryClient } from 'react-query';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { TouchpointDataProvider } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { SessionContextProvider } from 'common/components/TouchpointEditor/contexts/SessionsContext';
import { CustomCssContextProvider } from 'common/components/TouchpointEditor/contexts/CustomCssContext';
import { ModalManager } from 'common/components/TouchpointEditor/contexts/ModalManager';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';
import { AGENDA } from 'common/components/TouchpointEditor/constants/touchpoints';
import { TOUCHPOINT_TYPE_AGENDA } from 'common/constants/touchpointTypes';
import NotificationManager from 'common/compounds/NotificationManager';
import { LiveAnnouncer } from 'common/compounds/LiveAnnouncer/LiveAnnouncer';
import { AgendaGuestExperienceContainer } from './AgendaGuestExperienceContainer';
import { EVENT_MULTI_SESSION } from './constants';
import { SessionRegistrationStateProvider } from '../contexts/SessionRegistrationStateProvider';

/**
 * Get the bootstrap data that PHP left for us
 */
const bootstrap = getBootstrap();
const bootstrapHideFooter = bootstrap.get('event.hideFooter', false);
const { isCmsCodeEnabled } = bootstrap.get('flags');
const queryClient = new QueryClient();

function AgendaTouchpoint({ eventId, eventTitle, colors, activeFonts, logos, editable, hideFooter }) {
    const event = { id: eventId, title: eventTitle, multisession: EVENT_MULTI_SESSION, touchpoint: AGENDA };
    const theme = {
        colors,
        activeFonts,
        logos,
    };
    const { shouldPreview } = usePreview({ event });
    const isEditing = editable && !shouldPreview;

    return (
        <TouchpointDataProvider event={event} theme={theme}>
            <ModalManager>
                <QueryClientProvider client={queryClient}>
                    <NotificationManager>
                        <SessionContextProvider eventId={eventId} isHostView={editable}>
                            <CustomCssContextProvider
                                eventId={eventId}
                                section={TOUCHPOINT_TYPE_AGENDA}
                                isEditing={isEditing}
                                isCmsCodeEnabled={isCmsCodeEnabled}
                            >
                                <SessionRegistrationStateProvider>
                                    <LiveAnnouncer>
                                        <AgendaGuestExperienceContainer
                                            isEditing={isEditing}
                                            shouldPreview={shouldPreview}
                                            hideFooter={hideFooter}
                                        />
                                    </LiveAnnouncer>
                                </SessionRegistrationStateProvider>
                            </CustomCssContextProvider>
                        </SessionContextProvider>
                    </NotificationManager>
                </QueryClientProvider>
            </ModalManager>
        </TouchpointDataProvider>
    );
}

AgendaTouchpoint.propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eventTitle: PropTypes.string.isRequired,
    editable: PropTypes.bool,
    colors: PropTypes.arrayOf(PropTypes.string),
    activeFonts: PropTypes.arrayOf(PropTypes.string),
    logos: PropTypes.object,
    hideFooter: PropTypes.bool,
};

AgendaTouchpoint.defaultProps = {
    editable: false,
    colors: [],
    activeFonts: [],
    logos: {},
    hideFooter: bootstrapHideFooter,
};

export default AgendaTouchpoint;
